/**
 * An on/off switch (batch 17, notifications panel design §2.3): a `<button role="switch" aria-checked>`, named by a visible
 * label elsewhere (`labelledBy`; a `<label htmlFor={id}>` also toggles it on click). Space and Enter toggle it (native button
 * behaviour); focus never moves on toggle.
 *
 * Look: a 36×20 track with a 16 px knob. Off = dark track, knob left, a cross in the track; on = brand-blue track, knob right,
 * a tick. The glyphs are decorative (`aria-hidden`): the state is carried by the glyph and the knob's position, never by
 * colour alone, and by `aria-checked` for assistive technology. The hit target is at least 24×24 (44×44 on a phone).
 */
export function Switch({
  id,
  checked,
  onChange,
  labelledBy,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  labelledBy: string;
  disabled?: boolean;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      className="gf-switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      data-state={checked ? "on" : "off"}
      onClick={() => onChange(!checked)}
    >
      <span className="gf-switch-track" aria-hidden="true">
        <svg className="gf-switch-glyph" width="10" height="10" viewBox="0 0 10 10" focusable="false">
          {checked ? (
            <path d="M1.8 5.2l2.1 2.1 4.3-4.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M2.2 2.2l5.6 5.6M7.8 2.2l-5.6 5.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          )}
        </svg>
        <span className="gf-switch-knob" />
      </span>
    </button>
  );
}
